import { shortenedCompanyName } from "@fideus-core/company";
import { Box } from "@fideus-ui/core";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Helmet } from "react-helmet-async";
import { z } from "zod/v4";
import { useQuery, useTRPC } from "../../../_app/api";
import { CompanyDetailsLayout } from "../../../companies/CompanyDetailsLayout";

const searchSchema = z.object({
  jahr: z.coerce.number().optional(),
});

export const Route = createFileRoute("/firmen/$companyId")({
  validateSearch: zodValidator(searchSchema),
  loader: ({ context: { queryClient, trpc }, params: { companyId } }) => {
    void queryClient.ensureQueryData(
      trpc.companies.getCompany.queryOptions({ companyId })
    );
    void queryClient.ensureQueryData(
      trpc.annualStatements.listAnnualStatementsByCompanyId.queryOptions({
        companyId,
      })
    );
    void queryClient.ensureQueryData(trpc.getHubspotPortalId.queryOptions());
  },
  component: CompanyDetailsRoute,
});

function CompanyDetailsRoute() {
  const { companyId } = Route.useParams();
  const { jahr } = Route.useSearch();
  const navigate = useNavigate();
  const trpc = useTRPC();

  const { data: company } = useQuery(
    trpc.companies.getCompany.queryOptions({ companyId })
  );

  const { data: annualStatements } = useQuery(
    trpc.annualStatements.listAnnualStatementsByCompanyId.queryOptions({
      companyId,
    })
  );

  const { data: hubspotPortalIdData } = useQuery(
    trpc.getHubspotPortalId.queryOptions()
  );

  const sortedStatements = (annualStatements ?? []).sort(
    (a, b) => b.year - a.year
  );
  const years = sortedStatements.map((s) => s.year);
  const selectedYearStatus = jahr
    ? sortedStatements.find((s) => s.year === jahr)?.status
    : undefined;

  const hubspotUrl =
    company?.hubspotId && hubspotPortalIdData
      ? `https://app.hubspot.com/contacts/${hubspotPortalIdData.portalId}/company/${company.hubspotId}`
      : undefined;

  const gDriveUrl = company?.gDriveFolderId
    ? `https://drive.google.com/drive/folders/${company.gDriveFolderId}`
    : undefined;

  const onYearChange = (year: number | undefined) => {
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, jahr: year }),
    });
  };

  return (
    <Box
      component="main"
      sx={{
        paddingBottom: 1,
        bgcolor: "background.default",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {company?.name && (
        <Helmet>
          <title>
            {shortenedCompanyName(company.name)} - Fideus Service Portal
          </title>
        </Helmet>
      )}
      <CompanyDetailsLayout
        companyId={companyId}
        companyName={company?.name}
        hubspotUrl={hubspotUrl}
        gDriveUrl={gDriveUrl}
        selectedYear={jahr}
        selectedYearStatus={selectedYearStatus}
        years={years}
        onYearChange={onYearChange}
      />
      <Outlet />
    </Box>
  );
}
